import React, { useEffect } from 'react';
import App from './App';
import {
    ConfigurationProvider,
    useConfiguration
} from './contexts/configuration/ConfigurationContext';
import {
    ReactPlugin,
    AppInsightsContext
} from '@microsoft/applicationinsights-react-js';
import {
    ApplicationInsights,
    SeverityLevel
} from '@microsoft/applicationinsights-web';

// App Insights is a process-wide telemetry pipeline, not component state: one
// instance must outlive any remount, and re-creating it would tear down and
// re-init telemetry. Module scope expresses that directly, where the previous
// render-time ref lazy-init did not (and read refs during render).
const reactPlugin = new ReactPlugin();
let appInsights: ApplicationInsights | null = null;
let smokeTraceSent = false;

const getAppInsights = (connectionString?: string) => {
    if (!connectionString) return null;
    if (!appInsights) {
        appInsights = new ApplicationInsights({
            config: {
                connectionString,
                enableCorsCorrelation: true,
                enableRequestHeaderTracking: true,
                enableResponseHeaderTracking: true,
                enableAutoRouteTracking: true,
                extensions: [reactPlugin]
            }
        });
        appInsights.loadAppInsights();
    }
    return appInsights;
};

const AppWithMsal = () => {
    const { msalInstance, spaConfiguration } = useConfiguration();
    const connectionString = spaConfiguration.appInsightsConnectionString;

    useEffect(() => {
        const instance = getAppInsights(connectionString);

        // One-shot telemetry smoke test: proves the App Insights pipeline is
        // live when the Backoffice boots. Filter in the portal on the custom
        // property { event: 'bo-load-smoke-test' }. Remove once verified.
        if (!instance || smokeTraceSent) {
            return;
        }
        smokeTraceSent = true;
        instance.trackTrace(
            {
                message: 'Backoffice loaded',
                severityLevel: SeverityLevel.Information
            },
            { event: 'bo-load-smoke-test' }
        );
        instance.flush();
    }, [connectionString]);

    return (
        <>
            {msalInstance ? (
                <AppInsightsContext.Provider value={reactPlugin}>
                    <App pca={msalInstance} />
                </AppInsightsContext.Provider>
            ) : (
                <></>
            )}
        </>
    );
};

const AppRoot = () => {
    return (
        <ConfigurationProvider>
            <AppWithMsal />
        </ConfigurationProvider>
    );
};

export default AppRoot;

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//reportWebVitals();
